"use client";

import { useLocale } from "@i18n/react";
import type {
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
} from "@tanstack/react-query";
import { useInfiniteQuery } from "@tanstack/react-query";

import { useOpenApiClient } from "../../../providers/OpenApiProvider";
import { useSession } from "../../../session/hooks/useSession";
import { CURSOR_PATHS_SET } from "../../generated/cursorPaths";
import { GET_LAST_ID_PATHS_SET } from "../../generated/lastIdPaths";
import type { paths } from "../../generated/schema";
import { normalizeOpenApiError } from "./normalizeOpenApiError";
import type { GetParameters, GetPaths, GetResponse } from "./useOpenApiQuery";

type GetQuery<Path extends GetPaths> =
  GetParameters<Path> extends { query?: infer Query } ? Query : never;

/** Pagination by "page" */
type GetPagePaths = {
  [Path in GetPaths]: string extends keyof GetQuery<Path>
    ? never
    : "page" extends keyof GetQuery<Path>
      ? Path
      : never;
}[GetPaths];
/** Pagination by "last_id" */
type GetLastIdPaths = {
  [Path in GetPaths]: string extends keyof GetQuery<Path>
    ? never
    : "last_id" extends keyof GetQuery<Path>
      ? Path
      : never;
}[GetPaths];
type GetLastDatePaths = {
  [Path in GetPaths]: string extends keyof GetQuery<Path>
    ? never
    : "cursor" extends keyof GetQuery<Path>
      ? Path
      : never;
}[GetPaths];
export type GetPaginatedPaths =
  | GetPagePaths
  | GetLastIdPaths
  | GetLastDatePaths;

export type UseOpenApiInfiniteQueryOptions<Path extends GetPaginatedPaths> =
  Omit<UseInfiniteQueryOptions<GetResponse<Path>>, "queryFn"> & {
    enabledWithoutSessionToken?: boolean;
    params?: paths[Path]["get"]["parameters"];
    urlBackendPath: Path;
  };

export const useOpenApiInfiniteQuery = <Path extends GetPaginatedPaths>({
  enabled = true,
  enabledWithoutSessionToken = false,
  params,
  queryKey,
  urlBackendPath,
  ...options
}: UseOpenApiInfiniteQueryOptions<Path>) => {
  const { openApiClient } = useOpenApiClient();
  const locale = useLocale();
  const { token } = useSession();

  return useInfiniteQuery({
    enabled:
      (enabledWithoutSessionToken ? true : typeof token === "string") &&
      enabled,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await openApiClient.GET(urlBackendPath, {
        params: {
          ...params,
          query: {
            ...((params as { query?: Record<string, unknown> } | undefined)
              ?.query ?? {}),
            ...(GET_LAST_ID_PATHS_SET.has(urlBackendPath)
              ? { last_id: pageParam as number | null }
              : CURSOR_PATHS_SET.has(urlBackendPath)
                ? {
                    cursor: pageParam as string | null,
                  }
                : { page: pageParam as number }),
          },
        },
      } as never);
      if (error) throw normalizeOpenApiError(error);
      return data as GetResponse<Path>;
    },
    queryKey: queryKey ?? [urlBackendPath, params, locale],
    ...options,
  }) as UseInfiniteQueryResult<{
    pages: GetResponse<Path>[];
    pageParams: number[] | string[] | null[];
  }>;
};
